import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Table, Button, Spinner, Badge } from "react-bootstrap";
import { useAppContext } from "../../contexts/AppContext";

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { backendUrl, getImageUrl } = useAppContext();
  const userRoute = "/users";

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${backendUrl}${userRoute}`);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDelete = async (id, role, email) => {
    if (role === "superadmin") {
      alert("Cannot delete a superadmin account.");
      return;
    }

    if (window.confirm(`Are you sure you want to delete user ${email}?`)) {
      try {
        const token = localStorage.getItem("token");
        await axios.delete(`${backendUrl}${userRoute}/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchUsers();
      } catch (error) {
        console.error("Error deleting user:", error);
        alert("Failed to delete user.");
      }
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center p-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3 shadow-sm p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="fw-bold m-0">Admin Users Management</h5>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={fetchUsers}
          style={{ borderColor: "var(--primary-color)", color: "var(--primary-color)" }}
        >
          <i className="bi bi-arrow-clockwise me-1"></i> Refresh
        </Button>
      </div>

      <div className="table-responsive">
        <Table hover className="align-middle">
          <thead className="table-light">
            <tr>
              <th>User</th>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>
                  <div className="d-flex align-items-center">
                    <div
                      className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2 fw-bold overflow-hidden"
                      style={{ width: "40px", height: "40px", color: "var(--primary-color)" }}
                    >
                      {user.profilePhoto ? (
                        <img src={getImageUrl(user.profilePhoto)} alt="p" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        user.firstName?.charAt(0).toUpperCase()
                      )}
                    </div>
                    <div>
                      <div className="fw-bold">{user.firstName} {user.lastName}</div>
                      <div className="text-muted small">{user.sex || "Not specified"}</div>
                    </div>
                  </div>
                </td>
                <td>{user.userName}</td>
                <td>{user.email}</td>
                <td>
                  {user.role === "superadmin" ? (
                    <Badge bg="primary" className="rounded-pill px-3 py-2">Super Admin</Badge>
                  ) : (
                    <Badge bg="info-subtle" className="text-info border border-info border-opacity-25 rounded-pill px-3 py-2">Admin</Badge>
                  )}
                </td>
                <td className="text-end">
                  <div className="d-flex justify-content-end gap-2 flex-wrap">
                    <button
                      className="btn-action delete"
                      onClick={() => handleDelete(user.id, user.role, user.email)}
                      disabled={user.role === "superadmin"}
                      title="Delete User"
                    >
                      <i className="bi bi-trash3-fill"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <style>{`
        .btn-action {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          background: #f8fafc;
          cursor: pointer;
        }
        .btn-action.delete { color: #ef4444; }
        .btn-action.delete:hover:not(:disabled) { background: #ef4444; color: white; transform: translateY(-2px); shadow: 0 4px 12px rgba(239,68,68,0.2); }
        .btn-action:disabled { opacity: 0.5; cursor: not-allowed; }
      `}</style>
    </div>
  );
};

export default UserList;
